// Login.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { logAction, logEmployeeActivity } from "../utils/logsApi";
import Modal from "./Modal";
import "../App.css";
import { BASE_URL, HEADERS } from "../utils/config";

const Login = () => {
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
  });

  const navigate = useNavigate();

  // Prevent Enter key default when Modal is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && modal.isOpen) {
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modal.isOpen]);

  useEffect(() => {
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const openModal = (title, message) => {
    setModal({ isOpen: true, title, message });
  };

  const closeModal = () => {
    setModal({ isOpen: false, title: "", message: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent resubmission if modal is open
    if (modal.isOpen) {
      return;
    }

    if (!token.trim() || !email.trim()) {
      openModal("Validation Error", "Please enter a valid password and email.");
      logAction(
        "CLIENT_LOGIN_ATTEMPT",
        "UNKNOWN_USER",
        `Client attempted login with empty fields - Email: ${email || 'empty'}, Token: ${token ? 'provided' : 'empty'}`,
        null,
        "Authentication"
      );
      return;
    }

    setLoading(true);
    const API_URL = BASE_URL + "/api/login";

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: HEADERS,
        body: JSON.stringify({
          token: token.toLowerCase(),
          email: email.toLowerCase(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem("authToken", token);
        localStorage.setItem("userEmail", data.data.email);
        localStorage.setItem("userRole", data.data.role);
        openModal("Success", `Welcome, ${data.data.email}!`);
        
        // Log employee activity for successful login
        await logEmployeeActivity(
          "LOGIN",
          data.data.email,
          data.data.role,
          `Employee successfully logged in to admin panel`
        );
        
        logAction(
          "CLIENT_LOGIN_SUCCESS",
          data.data.email,
          `Client successfully logged in with role: ${data.data.role}`,
          null,
          "Authentication"
        );
        setTimeout(() => {
          closeModal();
          navigate("/dashboard");
        }, 1500);
      } else {
        openModal("Login Failed", data.message);
        
        // Log failed employee login attempt
        await logEmployeeActivity(
          "LOGIN_FAILED",
          email.toLowerCase(),
          "UNKNOWN",
          `Failed login attempt: ${data.message}`
        );
        
        logAction(
          "CLIENT_LOGIN_FAILED",
          email.toLowerCase(),
          `Client login failed: ${data.message}`,
          null,
          "Authentication"
        );
      }
    } catch (error) {
      console.error("Error during login:", error);
      openModal("Error", "An error occurred. Please try again later.");
      
      // Log employee login error
      await logEmployeeActivity(
        "LOGIN_ERROR",
        email.toLowerCase(),
        "UNKNOWN",
        `Login error occurred: ${error.message}`
      );
      
      logAction(
        "CLIENT_LOGIN_ERROR",
        email.toLowerCase(),
        `Client login error: ${error.message}`,
        null,
        "Authentication"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box dark-mode-box">
        <h1 className="login-title dark-mode-text">Login</h1>
        <form onSubmit={handleSubmit} className="login-form">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="login-input dark-mode-input"
          />
          <div className="password-container">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="login-input dark-mode-input password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="password-toggle"
              aria-label="Toggle Password Visibility"
            >
              {showPassword ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 4.5C7.5 4.5 3.75 7.75 2.25 12c1.5 4.25 5.25 7.5 9.75 7.5s8.25-3.25 9.75-7.5c-1.5-4.25-5.25-7.5-9.75-7.5zm0 12.75a4.5 4.5 0 110-9 4.5 4.5 0 010 9zm0-7.5a3 3 0 100 6 3 3 0 000-6z" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 4.5C7.5 4.5 3.75 7.75 2.25 12c1.5 4.25 5.25 7.5 9.75 7.5s8.25-3.25 9.75-7.5c-1.5-4.25-5.25-7.5-9.75-7.5zm0 12.75a4.5 4.5 0 110-9 4.5 4.5 0 010 9zm-6.75-3.75l1.5-1.5m11.25-4.5l-1.5 1.5" />
                </svg>
              )}
            </button>
          </div>
          <button
            type="submit"
            className="login-button dark-mode-button"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
      <Modal
        isOpen={modal.isOpen}
        title={modal.title}
        message={modal.message}
        onClose={closeModal}
      />
    </div>
  );
};

export default Login;
